import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Loader2, Check, Trash2, Plus } from "lucide-react";

/**
 * PricingPanel — admin-only live editor for service prices, vehicle surcharges
 * and additional services. Persists to MongoDB via PUT /api/admin/pricing.
 * Changes propagate immediately to public website (homepage + booking modal)
 * because all consumer components read /api/pricing/public.
 */

const TIER_LABELS = { classic: "CLASSIC", detailing: "PREMIUM" };
const SERVICE_LABELS = { exterior: "Exteriér", interior: "Interiér", complete: "Komplet" };
const VEHICLE_LABELS = { osobni: "Osobní", suv_mpv: "SUV / MPV", van: "Van" };

export default function PricingPanel() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [savedAt, setSavedAt] = useState(null);

  // Load once on mount
  useEffect(() => {
    setLoading(true);
    api.get("/admin/pricing")
      .then((r) => setCfg(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Nepodařilo se načíst ceník."))
      .finally(() => setLoading(false));
  }, []);

  const updateService = (tier, st, field, value) => {
    setCfg((c) => ({
      ...c,
      services: {
        ...c.services,
        [tier]: {
          ...c.services[tier],
          [st]: { ...c.services[tier][st], [field]: Number(value) || 0 },
        },
      },
    }));
  };

  const updateSurcharge = (vehicleId, percent) => {
    // Stored as 0..1, edited as % for UX
    const v = Math.max(0, Math.min(200, Number(percent) || 0)) / 100;
    setCfg((c) => ({ ...c, vehicleSurcharges: { ...c.vehicleSurcharges, [vehicleId]: v } }));
  };

  const updateExtra = (id, field, value) => {
    setCfg((c) => ({
      ...c,
      additionalServices: {
        ...c.additionalServices,
        [id]: { ...c.additionalServices[id], [field]: field === "price" ? Number(value) || 0 : value },
      },
    }));
  };

  const removeExtra = (id) => {
    if (!confirm(`Smazat příplatkovou službu „${cfg.additionalServices[id]?.name || id}"?`)) return;
    setCfg((c) => {
      const copy = { ...c.additionalServices };
      delete copy[id];
      return { ...c, additionalServices: copy };
    });
  };

  const addExtra = () => {
    const id = prompt('ID nové služby (anglicky, bez mezer, např. "wheels_polish"):');
    if (!id) return;
    const slug = id.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_");
    if (cfg.additionalServices[slug]) {
      alert("Tato ID už existuje.");
      return;
    }
    setCfg((c) => ({
      ...c,
      additionalServices: { ...c.additionalServices, [slug]: { name: "Nová služba", price: 0 } },
    }));
  };

  const save = async () => {
    setSaving(true); setErr("");
    try {
      const { data } = await api.put("/admin/pricing", {
        services: cfg.services,
        vehicleSurcharges: cfg.vehicleSurcharges,
        additionalServices: cfg.additionalServices,
      });
      setCfg(data);
      setSavedAt(new Date());
    } catch (e) {
      setErr(e?.response?.data?.detail || "Uložení selhalo.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-apex-gold" />;
  if (!cfg) return <div className="text-red-300">{err || "Nepodařilo se načíst ceník."}</div>;

  return (
    <div data-testid="pricing-panel">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <h3 className="font-display text-2xl text-apex-cream font-bold">Správa ceníku</h3>
          <p className="text-sm text-apex-cream/60 mt-1">
            Změny se po uložení projeví okamžitě na webu i v rezervačním formuláři.
            {cfg.updatedAt && (
              <span className="ml-2 text-apex-cream/45">
                Naposledy: {new Date(cfg.updatedAt).toLocaleString("cs-CZ")}{cfg.updatedBy ? ` · ${cfg.updatedBy}` : ""}
              </span>
            )}
          </p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="bg-apex-gold text-apex-bg font-bold px-5 py-2.5 rounded-xl inline-flex items-center gap-2 hover:bg-apex-gold-soft disabled:opacity-50"
          data-testid="pricing-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          {saving ? "Ukládám…" : "Uložit změny"}
        </button>
      </div>

      {err && (
        <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/40 text-sm text-red-300" data-testid="pricing-error">
          {err}
        </div>
      )}
      {savedAt && (
        <div className="mb-4 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/40 text-sm text-emerald-300">
          ✓ Uloženo {savedAt.toLocaleTimeString("cs-CZ")} — ceník je živý.
        </div>
      )}

      {/* === Services grid (2 tiers × 3 services) ===================== */}
      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        {["classic", "detailing"].map((tier) => (
          <div key={tier} className="rounded-2xl bg-apex-card border border-apex-line p-4" data-testid={`pricing-tier-${tier}`}>
            <h4 className="font-display text-lg text-apex-gold font-semibold mb-3 tracking-wider">{TIER_LABELS[tier]}</h4>
            <div className="space-y-2.5">
              {["exterior", "interior", "complete"].map((st) => (
                <div key={st} className="grid grid-cols-[1fr_auto_auto] gap-3 items-center">
                  <div className="text-sm text-apex-cream/85">{SERVICE_LABELS[st]}</div>
                  <PriceInput
                    value={cfg.services[tier][st].price}
                    onChange={(v) => updateService(tier, st, "price", v)}
                    suffix="Kč"
                    testId={`price-${tier}-${st}`}
                  />
                  <PriceInput
                    value={cfg.services[tier][st].duration}
                    onChange={(v) => updateService(tier, st, "duration", v)}
                    suffix="min"
                    testId={`duration-${tier}-${st}`}
                    minimal
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* === Vehicle surcharges ====================================== */}
      <div className="rounded-2xl bg-apex-card border border-apex-line p-4 mb-6" data-testid="pricing-vehicle-surcharges">
        <h4 className="font-display text-lg text-apex-gold font-semibold mb-3 tracking-wider">Příplatek dle typu vozidla</h4>
        <div className="grid sm:grid-cols-3 gap-3">
          {["osobni", "suv_mpv", "van"].map((v) => (
            <div key={v}>
              <label className="block text-xs text-apex-cream/60 mb-1.5">{VEHICLE_LABELS[v]}</label>
              <PriceInput
                value={Math.round((cfg.vehicleSurcharges[v] || 0) * 100)}
                onChange={(val) => updateSurcharge(v, val)}
                suffix="%"
                testId={`surcharge-${v}`}
                disabled={v === "osobni"}
              />
            </div>
          ))}
        </div>
      </div>

      {/* === Additional services ==================================== */}
      <div className="rounded-2xl bg-apex-card border border-apex-line p-4 mb-6" data-testid="pricing-extras">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-display text-lg text-apex-gold font-semibold tracking-wider">Příplatkové služby</h4>
          <button
            onClick={addExtra}
            className="text-xs text-apex-gold hover:underline inline-flex items-center gap-1"
            data-testid="pricing-add-extra"
          >
            <Plus className="w-3 h-3" /> Přidat
          </button>
        </div>
        <div className="space-y-2.5">
          {Object.entries(cfg.additionalServices || {}).map(([id, row]) => (
            <div key={id} className="grid grid-cols-[1fr_auto_auto] gap-3 items-center" data-testid={`pricing-extra-${id}`}>
              <input
                value={row.name || ""}
                onChange={(e) => updateExtra(id, "name", e.target.value)}
                className="bg-apex-bg/60 border border-apex-line rounded-lg px-3 py-2 text-sm text-apex-cream focus:outline-none focus:border-apex-gold/50"
                placeholder="Název"
              />
              <PriceInput
                value={row.price}
                onChange={(v) => updateExtra(id, "price", v)}
                suffix="Kč"
                testId={`pricing-extra-price-${id}`}
              />
              <button
                onClick={() => removeExtra(id)}
                className="text-red-300/80 hover:text-red-300 p-1.5"
                title="Smazat"
                data-testid={`pricing-extra-remove-${id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          {Object.keys(cfg.additionalServices || {}).length === 0 && (
            <div className="text-sm text-apex-cream/55 py-2">Žádné příplatkové služby. Klikněte na „Přidat".</div>
          )}
        </div>
      </div>
    </div>
  );
}

function PriceInput({ value, onChange, suffix, testId, minimal = false, disabled = false }) {
  return (
    <div className={`relative ${minimal ? "w-24" : "w-28"}`}>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={`w-full bg-apex-bg/60 border border-apex-line rounded-lg pl-2.5 pr-9 py-2 text-sm text-right text-apex-cream focus:outline-none focus:border-apex-gold/50 ${
          disabled ? "opacity-50 cursor-not-allowed" : ""
        }`}
        data-testid={testId}
      />
      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] uppercase text-apex-cream/55 font-semibold pointer-events-none">
        {suffix}
      </span>
    </div>
  );
}
